import { useMemo } from "react";
import {
  useCategoricalColors,
  type CategoricalColors,
  type WidgetSettingField,
} from "@salestrak/ui";
// Direct module path (not the feature barrel): definitions import this reader,
// and the barrel imports the Dashboard host → the app registry → definitions.
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import {
  useDimensionCounts,
  WIDGET_DIMENSION_OPTIONS,
  type DimensionSlice,
  type WidgetDimension,
} from "#app/widgets/dashboards/use-dimension-counts";
import { resolveWidgetFilters } from "#app/widgets/dashboards/widget-filters";

/** Dashboards count live work — archived projects are out unless a widget's own
 *  filters opt them back in. THE shared base `where`; every widget data hook
 *  imports this, never a local copy. */
export const ARCHIVED_OUT = { archived: { eq: false } };

/**
 * THE shared reader for every dimension-sliced count widget (pie / bar /
 * battery / treemap): counts projects grouped by the chosen dimension, ranked
 * high→low, over the ambient dashboard window narrowed by the widget's own
 * Filters blob (`resolveWidgetFilters`: archived-out unless shown, saved query,
 * widget-owned date range). A widget NEVER re-plumbs the scope fold — it calls
 * this and renders the slices however it likes.
 */
export function useDimensionSlices(
  groupBy: WidgetDimension,
  filters?: unknown,
  dateField: "creationDate" | "lastUpdateDate" = "creationDate",
) {
  const { base, override } = resolveWidgetFilters(filters);
  const { where } = useScopedWhere(base, { dateField, override });
  return useDimensionCounts(groupBy, where);
}

/** How many slices a widget keeps before the tail folds into a single "Other". */
export type SliceShow = "top5" | "top8" | "top12" | "all";
export type SliceSort = "volume" | "alpha";

/** The shared "Show" select options — one vocabulary across pie / bar / battery. */
export const SLICE_SHOW_OPTIONS: { value: SliceShow; label: string }[] = [
  { value: "top5", label: "Top 5" },
  { value: "top8", label: "Top 8" },
  { value: "top12", label: "Top 12" },
  { value: "all", label: "All groups" },
];

/** The shared "Sort by" select options. */
export const SLICE_SORT_OPTIONS: { value: SliceSort; label: string }[] = [
  { value: "volume", label: "Most projects" },
  { value: "alpha", label: "Alphabetical" },
];

/**
 * The Data-section trio (Group by / Show / Sort by) every slice widget spreads
 * into its own `settings` — one vocabulary, one icon set, still opt-in per
 * module (a widget with a bespoke Data section just doesn't spread it).
 */
export const SLICE_DATA_FIELDS: WidgetSettingField[] = [
  {
    key: "groupBy",
    kind: "select",
    label: "Group by",
    icon: "layers",
    options: WIDGET_DIMENSION_OPTIONS,
  },
  { key: "show", kind: "select", label: "Show", icon: "eye", options: SLICE_SHOW_OPTIONS },
  {
    key: "sort",
    kind: "select",
    label: "Sort by",
    icon: "arrow-down-wide-narrow",
    options: SLICE_SORT_OPTIONS,
  },
];

/** The Gridlines / Axes toggle pair every XY-chart widget spreads into Display. */
export const AXES_DISPLAY_FIELDS: WidgetSettingField[] = [
  { key: "showGrid", kind: "toggle", label: "Gridlines", icon: "grid-dots" },
  { key: "showAxes", kind: "toggle", label: "Axes", icon: "ruler" },
];

const SHOW_LIMIT: Record<SliceShow, number> = {
  top5: 5,
  top8: 8,
  top12: 12,
  all: Number.POSITIVE_INFINITY,
};

/**
 * Fold everything past the chosen limit into one "Other" slice, then apply the
 * display sort — so a pie's wedges, a battery's segments, and a bar's columns
 * all stay a bounded, readable set that still sums to the whole. "Top N" keeps
 * exactly N NAMED slices; "Other" is the extra (N+1th) slice, never a
 * substitute for the Nth-ranked category. The fold always happens on the
 * volume-desc order the reader returns ("Other" is the low-volume tail);
 * alpha sort is presentation only and keeps "Other" last.
 */
export function shapeSlices(
  data: DimensionSlice[],
  sort: SliceSort,
  show: SliceShow,
): DimensionSlice[] {
  const max = SHOW_LIMIT[show];
  let shaped = data;
  if (data.length > max) {
    const head = data.slice(0, max);
    const tail = data.slice(max);
    const other = tail.reduce((sum, d) => sum + d.total, 0);
    // "Other" is a mixed tail, not an entity — it always wears the muted grey,
    // never a ramp hue that would read as one more category.
    shaped = [...head, { id: "__other__", label: "Other", total: other, color: "grey" }];
  }
  if (sort === "alpha") {
    const named = shaped.filter((s) => s.id !== "__other__");
    const other = shaped.filter((s) => s.id === "__other__");
    shaped = [...named.sort((a, b) => a.label.localeCompare(b.label)), ...other];
  }
  return shaped;
}

/**
 * The full read→shape→color plumbing for a slice widget in one call: the shared
 * reader over `groupBy`, the Show/Sort fold, and THE categorical assignment
 * (`sliceColors`, index-aligned with `slices`) — entity-owned palette color
 * first, ramp fallback for colorless slices, "Other" muted. Marks take
 * `sliceColors.tokens[i]`, series accessors `sliceColors.colors[i]` or
 * `.scale(label)`, legends `.scale` — so segments, wedges, bars, and legends
 * all color alike AND a stage keeps its color across every widget. Takes the
 * widget's config directly — every slice widget's schema carries
 * `groupBy`/`sort`/`show`. `data` is the unfolded set (for `sliceDrilldownIds`);
 * rendering the slices stays entirely the widget's own.
 */
export function useShapedSlices(config: {
  groupBy: WidgetDimension;
  sort: SliceSort;
  show: SliceShow;
  filters?: unknown;
  dateField?: "creationDate" | "lastUpdateDate";
}): {
  data: DimensionSlice[];
  slices: DimensionSlice[];
  sliceColors: CategoricalColors;
  loading: boolean;
  error: unknown;
} {
  const { data, loading, error } = useDimensionSlices(
    config.groupBy,
    config.filters,
    config.dateField,
  );
  const slices = useMemo(
    () => shapeSlices(data, config.sort, config.show),
    [data, config.sort, config.show],
  );
  const sliceColors = useCategoricalColors(slices);
  return { data, slices, sliceColors, loading, error };
}

/**
 * The covered category ids for a (possibly folded) slice press → a widget-owned
 * drilldown: a real slice covers its own id; the "Other" wedge covers every id
 * in `all` that isn't among the `shown` head slices.
 */
export function sliceDrilldownIds(
  slice: DimensionSlice,
  all: DimensionSlice[],
  shown: DimensionSlice[],
): string[] {
  if (slice.id !== "__other__") return [slice.id];
  const head = new Set(shown.filter((s) => s.id !== "__other__").map((s) => s.id));
  return all.filter((d) => !head.has(d.id)).map((d) => d.id);
}

export type { DimensionSlice, WidgetDimension };
